import { getParam } from '../../../ts/helper';
import { atomDownloadTeaser } from '../../atoms/atomDownloadTeaser';
import { atomIcon } from '../../atoms/atomIcon';
import { aButton } from '../../atoms/button';

/**
 * Friendation component
 * @param component Instance of this _Friendation_ component
 */
const blockArchiveDownloadsFunction = async (component: HTMLElement) => {
  const form = component.querySelector('form');
  if (!form) return;

  const searchResultsList = <HTMLElement>(
    component.querySelector('.searchResults-list')
  );
  if (!searchResultsList) return;

  /** Button that will reset the form */
  const resetButton = <HTMLButtonElement>(
    component.querySelector('[type="reset"]')
  );

  /**
   * Parameter keys in the URL
   * @example
   *   "/?id=123&q=foo"
   */
  const searchParams = {
    downloadId: 'id',
    categorySlug: 'category',
    term: 'q',
    fileType: 'type',
    sort: 'sortBy'
  };

  /** The search term that is used to display the currently visible items. Changes after a search was done. */
  let currentSearchTerm = getParam(searchParams.term);
  /** Mirrors the search term in the URL. Changes before a search is done on every input. */
  let updatedSearchTerm = getParam(searchParams.term);

  /** Indicates that the first data we see on page load was delivered by Craft with Twig. No need to fetch new data now. Set this to `false` once the first load was done, so we make sure to fetch new data from then on. */
  let firstLoad = true;

  /**
   * Show a message in the frontend, depending on the current state
   */
  const showMessage = (key: 'loading' | 'noResults' | 'results') => {
    if (key === 'loading') {
      form.setAttribute('aria-busy', 'true');
    } else {
      form.removeAttribute('aria-busy');
    }
    const allMessages: HTMLElement[] = Array.from(
      component.querySelectorAll('.message')
    );
    const targetMessage: HTMLElement = component.querySelector(
      `.message--${key}`
    );

    const updateResultsCount = () => {
      const message = allMessages.find((x) =>
        x.classList.contains('message--results')
      );

      const count = getResults()
        .filter((x) => !x.inert)
        .length.toString();
      message.querySelector('h2').innerText = message
        .getAttribute('data-text')
        .replace('%results%', count);
    };

    updateResultsCount();

    allMessages.forEach((x) => (x.inert = true));
    targetMessage.inert = false;
  };

  /**
   * On {@link form} reset
   */
  const onReset = () => {
    form.setAttribute('aria-busy', 'true');

    const url = new URL(window.location.href);
    url.search = '';
    window.history.pushState({}, '', url.href);
    window.location.reload();

    resetButton.hidden = true;
  };

  /**
   * On {@link form} submit
   */
  const onSubmit = (e: SubmitEvent) => {
    e.preventDefault();

    if (currentSearchTerm && !updatedSearchTerm) {
      // Reset (user clicked on the webkit cancel button and then searched)
      onReset();
      return;
    }

    // Search
    updateView();
    resetButton.hidden = false;
  };

  /**
   * @returns All items in the {@link searchResultsList} regardless of their state
   */
  const getResults = () =>
    <HTMLElement[]>(
      Array.from(searchResultsList.querySelectorAll(':scope > li'))
    );

  /**
   * Runs a Craft query based on the current URL (which contains all filters and search terms in real time)
   */
  const runSearchQuery = async () => {
    console.info('🚀 Fetching new data...');

    const tempDom = document.createElement('body');
    const response = await fetch(window.location.href, {}).then((x) =>
      x.text()
    );
    tempDom.innerHTML = response;

    const results = Array.from(
      tempDom.querySelectorAll('.searchResults-list > li')
    );
    searchResultsList.innerHTML = '';

    results.forEach((result) => {
      searchResultsList.append(result.cloneNode(true));
    });

    atomIcon();
    aButton();
  };

  const updateView = () => {
    /** When the GET parameter {@link searchParams.downloadId} is present, we only want to display this specific item. This will mainly be used to preview entries from the backend in the frontend. */
    const downloadId = <string>getParam(searchParams.downloadId);

    /** When the GET parameter {@link searchParams.categorySlug} is set, we want to display all items that are related to that category */
    const categorySlug = <string>getParam(searchParams.categorySlug);

    /** When the {@link searchParams.term} parameter is set, we want to filter by text. */
    const term = <string>getParam(searchParams.term);

    /** When the {@link searchParams.fileType} parameter is set, we want to filter by text. */
    const fileType = <string>getParam(searchParams.fileType);

    showMessage('loading');

    if (!term) {
      // Clear term from url when empty to avoid false Craft requests
      const url = new URL(window.location.href);
      url.searchParams.delete(searchParams.term);
      window.history.pushState({}, '', url.href);
    }

    /**
     * Filter all the {@link getResults} by all the filters that we have!
     */
    const filterResults = async () => {
      if (firstLoad) {
        // not on page load
        firstLoad = false;
      } else {
        if (currentSearchTerm !== updatedSearchTerm) {
          /** The search term has changed: load fresh data */
          await runSearchQuery();
          currentSearchTerm = getParam(searchParams.term);
        }
      }

      const items = getResults();

      // Show all
      items.forEach((item) => {
        item.inert = false;
      });

      /**
       * Hide all {@link items}, that do not match the required criteria, meaning not having a matching attribute and value pair.
       * @param key
       * @param value
       */
      const hideResults = (key: string, value: string) => {
        items
          .filter((x) => x.getAttribute(key) !== value)
          .forEach((x) => {
            x.inert = true;
          });
      };

      if (downloadId) hideResults('data-id', downloadId);
      if (categorySlug) hideResults('data-category', categorySlug);
      if (fileType) hideResults('data-file-type', fileType);

      /** no need to filter by term. We do that with Craft and {@link runSearchQuery} */
      // if (term) {}

      /** After hiding all {@link items} we do not want, we can display messages and show/hide the {@link searchResultsList} */
      setTimeout(() => {
        if (getResults().filter((x) => !x.inert).length) {
          searchResultsList.inert = false;
          showMessage('results');
          atomDownloadTeaser();
        } else {
          showMessage('noResults');
          searchResultsList.inert = true;
        }
      }, 700);
    };

    /**
     * Sort the {@link getResults} by all the sorting fields we have
     */
    const sortResults = () => {
      const sortAlgorithm = <'*' | 'alphaAsc' | 'alphaDesc'>(
        getParam(searchParams.sort)
      );

      const items = getResults();
      if (!items || !items.length) return;

      const container = items[0].parentElement;

      if (sortAlgorithm === 'alphaAsc') {
        items.sort((a, b) => {
          const titleB = (b.getAttribute('data-title') || '')
            .trim()
            .toLowerCase();
          const titleA = (a.getAttribute('data-title') || '')
            .trim()
            .toLowerCase();
          return titleA.localeCompare(titleB); // Ascending order
        });
      } else if (sortAlgorithm === 'alphaDesc') {
        items.sort((a, b) => {
          const titleA = (a.getAttribute('data-title') || '')
            .trim()
            .toLowerCase();
          const titleB = (b.getAttribute('data-title') || '')
            .trim()
            .toLowerCase();
          return titleB.localeCompare(titleA); // Descending order
        });
      } else {
        items.sort((a, b) => {
          const indexA = parseInt(a.getAttribute('data-index'));
          const indexB = parseInt(b.getAttribute('data-index'));
          return indexA - indexB; // Numeric sorting by data-index
        });
      }

      // Clear the container
      container.innerHTML = '';

      // Append sorted items back to the container
      items.forEach((item) => container.appendChild(item));
    };

    setTimeout(() => {
      filterResults();
      sortResults();
    }, 100);
  };

  /**
   * A user can filter results by a single category
   */
  const handleFilterByCategory = () => {
    const input: HTMLSelectElement = form.querySelector(
      "[name='filterByCategory']"
    );

    /** Value on first page load in URL */
    const initialValue = <string>getParam(searchParams.categorySlug);

    // Set initial value
    if ((!input.value || input.value === '*') && initialValue) {
      if (input.querySelector(`[value="${input.value}"]`)) {
        input.value = initialValue;
      } else {
        // the initial value in the url does not exist
        const url = new URL(window.location.href);
        url.searchParams.delete(searchParams.categorySlug);
        window.history.pushState({}, '', url.href);
      }
    }

    /**
     * Sync the value of the {@link input} with the URL
     */
    const onInput = () => {
      const value = input?.value.trim();
      const url = new URL(window.location.href);
      if (input.value === '*') {
        url.searchParams.delete(searchParams.categorySlug);
      } else {
        url.searchParams.set(searchParams.categorySlug, value);
      }
      window.history.pushState({}, '', url.href);
      form.dispatchEvent(new Event('submit'));
    };

    input.addEventListener('change', onInput);
  };

  /**
   * A user can filter results by a single file type like `"pdf"`
   */
  const handleFilterByFileType = () => {
    const input: HTMLSelectElement = form.querySelector(
      "[name='filterByFileType']"
    );

    const buildOptions = () => {
      const fileTypes = [];

      getResults().forEach((x) => {
        const fileType = x.getAttribute('data-file-type').trim();
        if (!fileTypes.includes(fileType)) fileTypes.push(fileType);
      });

      fileTypes.forEach((fileType) => {
        const option = document.createElement('option');
        option.value = fileType;
        option.innerText = fileType;
        input.append(option);
      });
    };

    /** Value on first page load in URL */
    const initialValue = <string>getParam(searchParams.fileType);

    buildOptions();

    // Set initial value
    if ((!input.value || input.value === '*') && initialValue) {
      if (input.querySelector(`[value="${input.value}"]`)) {
        input.value = initialValue;
      } else {
        // the initial value in the url does not exist
        const url = new URL(window.location.href);
        url.searchParams.delete(searchParams.fileType);
        window.history.pushState({}, '', url.href);
      }
    }

    /**
     * Sync the value of the {@link input} with the URL
     */
    const onInput = () => {
      const value = input?.value.trim();
      const url = new URL(window.location.href);
      if (input.value === '*') {
        url.searchParams.delete(searchParams.fileType);
      } else {
        url.searchParams.set(searchParams.fileType, value);
      }
      window.history.pushState({}, '', url.href);
      form.dispatchEvent(new Event('submit'));
    };

    input.addEventListener('change', onInput);
  };

  /**
   * A user can search for text
   */
  const handleSearch = () => {
    const input: HTMLInputElement = form.querySelector("[type='search']");

    /** Value on first page load in URL */
    const initialValue = <string>getParam(searchParams.term);

    // Set initial value
    if (!input.value && initialValue) {
      input.value = initialValue;
    }

    /**
     * Sync the value of the {@link input} with the URL
     */
    const onInput = () => {
      const value = input?.value.trim();
      updatedSearchTerm = value;
      const url = new URL(window.location.href);
      url.searchParams.set(searchParams.term, value);
      window.history.pushState({}, '', url.href);
    };

    input.addEventListener('input', onInput);
  };

  const handleSort = () => {
    const input: HTMLSelectElement = form.querySelector("[name='sortBy']");

    /** Value on first page load in URL */
    const initialValue = <string>getParam(searchParams.sort);

    // Set initial value
    if ((!input.value || input.value === '*') && initialValue) {
      if (input.querySelector(`[value="${input.value}"]`)) {
        input.value = initialValue;
      } else {
        // the initial value in the url does not exist
        const url = new URL(window.location.href);
        url.searchParams.delete(searchParams.sort);
        window.history.pushState({}, '', url.href);
      }
    }

    /**
     * Sync the value of the {@link input} with the URL
     */
    const onInput = () => {
      const value = input?.value.trim();
      const url = new URL(window.location.href);
      if (input.value === '*') {
        url.searchParams.delete(searchParams.sort);
      } else {
        url.searchParams.set(searchParams.sort, value);
      }
      window.history.pushState({}, '', url.href);
      form.dispatchEvent(new Event('submit'));
    };

    input.addEventListener('change', onInput);
  };

  const handleLayoutView = () => {
    const container: HTMLElement = component.querySelector('.searchResults');
    if (!container) return;

    const radios: HTMLInputElement[] = Array.from(
      component.querySelectorAll('.viewToggle input')
    );

    const onChange = () => {
      const value = radios.find((x) => x.checked).value;
      container.dataset.view = value;
    };

    radios.forEach((radio) => {
      radio.addEventListener('change', onChange);
    });
  };

  handleFilterByCategory();
  handleFilterByFileType();
  handleSort();
  handleSearch();
  handleLayoutView();
  updateView();

  form.addEventListener('submit', onSubmit.bind(this));
  form.addEventListener('reset', onReset.bind(this));
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-archive-downloads"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void =
  blockArchiveDownloadsFunction;

/**
 * Friendation component
 */
export const blockArchiveDownloads = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((x) => {
    classFunction.call(this, x);
  });
};
